import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import SearchBar from './SearchBar/SearchBar';
import SearchButton from './SearchButton/SearchButton';
import CountryDropdown from './CountryDropdown/CountryDropdown';
import { useSearchPersistence } from '../../../../hooks/useSearchPersistence'; // Importando o hook
import FilterDropdown from '../../../../components/FilterDropdown/FilterDropdown'; // Importando o dropdown genérico
import { useTranslation } from 'react-i18next';

export interface SearchControls {
  rating: number; // 0 to 5
  whatsappStatus: 'all' | 'verified' | 'unverified_pending'; // Alterado para incluir 'unverified_pending'
  sortBy: 'relevance' | 'name_asc' | 'name_desc' | 'rating_desc' | 'rating_asc';
  resultsPerPage: number;
}

interface SearchSectionProps {
  onSearch: (query: string, countryCode: string, controls: SearchControls) => void;
  isLoading: boolean; // Nova prop
}

const SearchSection: React.FC<SearchSectionProps> = ({ onSearch, isLoading }) => {
  const { t } = useTranslation();
  const { searchContext } = useSearchPersistence();

  // Inicializa o estado com valores persistidos ou padrões
  const initialQuery = searchContext?.query || '';
  const initialCountryCode = searchContext?.countryCode || 'br';
  const initialControls = searchContext?.controls || {
    rating: 0,
    whatsappStatus: 'all',
    sortBy: 'relevance',
    resultsPerPage: 20,
  };

  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [countryCode, setCountryCode] = useState(initialCountryCode);
  // Mantemos todos os controles no estado, mas só expomos resultsPerPage no UI desta seção
  const [controls, setControls] = useState<SearchControls>(initialControls);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  // Se o contexto mudar externamente (ex: ao voltar para a página), atualiza os inputs
  useEffect(() => {
    if (searchContext) {
      setSearchQuery(searchContext.query);
      setCountryCode(searchContext.countryCode);
      setControls(searchContext.controls);
    }
  }, [searchContext]);


  const handleSearch = () => {
    // Passamos a query e o código do país
    onSearch(searchQuery, countryCode, controls);
    if (process.env.NODE_ENV === 'development') {
      console.log('Current Search Controls:', controls);
    }
  };

  // --- Results Per Page Dropdown Logic ---
  const perPageOptions = [20, 40, 60, 100];

  const handlePerPageSelect = (label: string) => {
    const value = parseInt(label.split(' ')[0]);
    setControls(prev => ({ ...prev, resultsPerPage: value }));
    setOpenDropdown(null);
  };

  const getPerPageLabel = (value: number) => {
    return t('{{count}} por página', { count: value });
  };

  // A função de limpar filtros não é mais necessária aqui, pois os filtros foram movidos.
  // Apenas limpamos a query e o país se necessário.

  return (
    <motion.div
      data-tour="search-section"
      className="rounded-xl bg-white dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 p-6 shadow-sm card relative"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1, duration: 0.5 }}
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="md:col-span-2">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            onSearch={handleSearch}
          />
        </div>
        <CountryDropdown
          countryCode={countryCode}
          onSelect={setCountryCode}
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
        {/* Results Per Page Dropdown (Mantido aqui) */}
        <FilterDropdown
          type="resultsPerPage"
          label={getPerPageLabel(controls.resultsPerPage)}
          options={perPageOptions.map(p => getPerPageLabel(p))}
          selectedValue={getPerPageLabel(controls.resultsPerPage)}
          onSelect={handlePerPageSelect}
          isOpen={openDropdown === 'resultsPerPage'}
          onToggle={() => setOpenDropdown(openDropdown === 'resultsPerPage' ? null : 'resultsPerPage')}
        />

        <SearchButton onClick={handleSearch} isLoading={isLoading} />
      </div>
    </motion.div>
  );
};

export default SearchSection;